import classNames from "classnames";
import React, { FC, ReactNode, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  LuArrowLeft,
  LuCheck,
  LuCopy,
  LuMoreHorizontal,
  LuPencil,
  LuRotateCcw,
  LuStar,
  LuTrash2,
  LuUsers,
} from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";

import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import {
  useCreateCustomReportMutation,
  useDeleteCustomReportMutation,
  useUpdateCustomReportMutation,
} from "../../../slices/CustomReportsSlice";
import { aiErrorMessage } from "../../ai/ai.utils";
import { useReportsAccess } from "../reportsAccess";
import { changedParts, STANDARD_REPORTS, viewChanged } from "./savedView";
import { useSavedView } from "./SavedViewContext";

const BTN =
  "inline-flex h-10 items-center gap-2 rounded-[10px] border border-[#DCE2EA] bg-white px-4 text-[13px] font-bold text-[#1F2433] hover:border-[#6B4EFF] disabled:opacity-50";
const BTN_PRIMARY =
  "inline-flex h-10 items-center gap-2 rounded-[10px] bg-[#6B4EFF] px-4 text-[13px] font-bold text-white hover:bg-[#5A3EE6] disabled:opacity-50";
const TAG =
  "inline-flex items-center gap-1 rounded-full px-2 py-[3px] text-[11px] font-bold";

/** Name window: "Save as my report", "Rename", "Save a copy". */
export const NameDialog: FC<{
  title: string;
  description?: string;
  initialName: string;
  chips?: string[];
  showShare?: boolean;
  saveLabel?: string;
  busy?: boolean;
  error?: string;
  onSave: (name: string, shared: boolean) => void;
  onCancel: () => void;
}> = ({
  title,
  description,
  initialName,
  chips,
  showShare,
  saveLabel = "Save",
  busy,
  error,
  onSave,
  onCancel,
}) => {
  const [name, setName] = useState(initialName);
  const [shared, setShared] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  const trimmed = name.trim();
  const submit = () => trimmed && !busy && onSave(trimmed, shared);
  return ReactDOM.createPortal(
    <div
      className={
        "fixed inset-0 z-[1000] flex items-start justify-center bg-[rgba(20,22,34,0.35)] px-4 pt-[12vh] font-Manrope"
      }
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={title}
        className={
          "w-full max-w-[520px] rounded-[16px] bg-white px-6 py-[22px] text-[#1F2433] shadow-[0_20px_50px_rgba(16,24,40,0.25)]"
        }>
        <h2 className={"text-lg font-extrabold"}>{title}</h2>
        {description && (
          <p className={"mb-4 mt-1 text-[13px] text-[#5B6472]"}>
            {description}
          </p>
        )}
        <label
          htmlFor={"saved-report-name"}
          className={"mb-1.5 block text-[12.5px] font-bold text-[#3A4256]"}>
          Report name
        </label>
        <input
          id={"saved-report-name"}
          autoFocus
          maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className={
            "h-[42px] w-full rounded-[10px] border border-[#DCE2EA] px-3 text-sm outline-none focus:border-[#6B4EFF] focus:shadow-[0_0_0_3px_#EEEBFF]"
          }
        />
        {!!chips?.length && (
          <div
            className={
              "mt-3.5 rounded-xl border border-[#EDEFF3] bg-[#F7F8FA] px-3.5 py-3"
            }>
            <div
              className={
                "mb-2 text-[11px] font-bold uppercase tracking-[0.05em] text-[#9097A8]"
              }>
              Saved with it
            </div>
            <div className={"flex flex-wrap gap-1.5"}>
              {chips.map((c) => (
                <span
                  key={c}
                  className={
                    "rounded-full border border-[#E4E7EC] bg-white px-2.5 py-1 text-xs font-semibold text-[#3A4256]"
                  }>
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}
        {showShare && (
          <label
            className={
              "mt-4 flex cursor-pointer items-start gap-2.5 text-[13.5px]"
            }>
            <input
              type={"checkbox"}
              className={"mt-[3px] h-4 w-4 accent-[#6B4EFF]"}
              checked={shared}
              onChange={(e) => setShared(e.target.checked)}
            />
            <span>
              <b>Share with my team</b>
              <span className={"block text-[12.5px] text-[#5B6472]"}>
                Everyone in the firm sees it in Reports. Each person only sees
                the numbers their access allows.
              </span>
            </span>
          </label>
        )}
        {error && (
          <div
            role={"alert"}
            className={
              "mt-3 rounded-[10px] border border-[#FAD4D0] bg-[#FEF3F2] px-3 py-2 text-[13px] font-semibold text-[#B42318]"
            }>
            {error}
          </div>
        )}
        <div className={"mt-5 flex justify-end gap-2"}>
          <button type={"button"} className={BTN} onClick={onCancel}>
            Cancel
          </button>
          <button
            type={"button"}
            className={BTN_PRIMARY}
            disabled={!trimmed || busy}
            onClick={submit}>
            {busy ? "Saving…" : saveLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

type Dialog = "save" | "rename" | "copy" | null;

/**
 * "Save as my report" on a standard report, and on a saved one: name + "Based on" header,
 * "Save changes", the changed-filters note with Undo, and the ⋯ menu.
 */
export const useSavedViewControls = (
  description: string,
): {
  header?: ReactNode;
  actions: ReactNode;
  note: ReactNode;
  dialogs: ReactNode;
} | null => {
  const ctx = useSavedView();
  const navigate = useNavigate();
  const access = useReportsAccess();
  const orgId = getLinkedFirm()?.orgId || "";
  const [dialog, setDialog] = useState<Dialog>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [create, createCall] = useCreateCustomReportMutation();
  const [update, updateCall] = useUpdateCustomReportMutation();
  const [remove] = useDeleteCustomReportMutation();
  if (!ctx) return null;

  const info = STANDARD_REPORTS[ctx.report];
  const view = ctx.snapshot();
  const saved = ctx.saved;
  const changed = !!saved && !!ctx.initial && viewChanged(ctx.initial, view);
  const busy = createCall.isLoading || updateCall.isLoading;
  const close = () => {
    setDialog(null);
    setError("");
  };

  const saveNew = async (name: string, shared: boolean) => {
    setError("");
    try {
      const report = await create({
        orgId,
        name,
        description: info?.name,
        request: "",
        spec: view,
        shared,
      }).unwrap();
      close();
      navigate(`/reports/saved/${report.id}`);
    } catch (e) {
      setError(aiErrorMessage(e));
    }
  };
  const saveChanges = async () => {
    if (!saved) return;
    setMessage("");
    try {
      await update({ id: saved.id, orgId, spec: view }).unwrap();
      setMessage("Changes saved.");
    } catch (e) {
      setMessage(aiErrorMessage(e));
    }
  };
  const rename = async (name: string) => {
    if (!saved) return;
    try {
      await update({ id: saved.id, orgId, name }).unwrap();
      close();
    } catch (e) {
      setError(aiErrorMessage(e));
    }
  };
  const share = async () => {
    if (!saved) return;
    setMenuOpen(false);
    try {
      await update({ id: saved.id, orgId, shared: !saved.shared }).unwrap();
      setMessage(
        saved.shared
          ? "Only you can see this report now."
          : "Your team can now see this report in Reports.",
      );
    } catch (e) {
      setMessage(aiErrorMessage(e));
    }
  };
  const onDelete = async () => {
    if (!saved) return;
    setMenuOpen(false);
    if (!window.confirm(`Delete “${saved.name}”? This can't be undone.`))
      return;
    try {
      await remove({ id: saved.id, orgId }).unwrap();
      navigate("/reports");
    } catch (e) {
      setMessage(aiErrorMessage(e));
    }
  };

  const dialogs =
    dialog === "save" ? (
      <NameDialog
        title={"Save as my report"}
        description={
          "Saves this report with the filters you set, so you can open it again in one click."
        }
        initialName={info?.name ?? ""}
        chips={[`Report: ${info?.name}`, ...view.labels]}
        showShare
        saveLabel={"Save report"}
        busy={busy}
        error={error}
        onSave={saveNew}
        onCancel={close}
      />
    ) : dialog === "copy" ? (
      <NameDialog
        title={"Save a copy"}
        description={
          "Saves a copy in your own reports, with the filters as they are now."
        }
        initialName={`${saved?.name ?? info?.name} (copy)`.slice(0, 80)}
        chips={[`Report: ${info?.name}`, ...view.labels]}
        showShare
        saveLabel={"Save copy"}
        busy={busy}
        error={error}
        onSave={saveNew}
        onCancel={close}
      />
    ) : dialog === "rename" && saved ? (
      <NameDialog
        title={"Rename report"}
        initialName={saved.name}
        saveLabel={"Rename"}
        busy={busy}
        error={error}
        onSave={rename}
        onCancel={close}
      />
    ) : null;

  if (!saved) {
    return {
      actions: (
        <button
          type={"button"}
          className={BTN}
          onClick={() => setDialog("save")}>
          <LuStar size={15} /> Save as my report
        </button>
      ),
      note: null,
      dialogs,
    };
  }

  const parts = changed && ctx.initial ? changedParts(ctx.initial, view) : [];
  const header = (
    <div className={"min-w-0"}>
      <div
        className={
          "flex items-center gap-1 text-[13px] font-semibold text-[#6B7185]"
        }>
        <Link
          to={"/reports"}
          className={
            "inline-flex items-center gap-1 font-bold text-[#6B4EFF] hover:text-[#5236E8]"
          }>
          <LuArrowLeft size={14} strokeWidth={2.4} />
          Reports
        </Link>
        <span>›</span>
        <span>My reports</span>
      </div>
      <div className={"mt-1.5 flex flex-wrap items-center gap-2.5"}>
        <h1 className={"text-[26px] font-extrabold leading-tight"}>
          {saved.name}
        </h1>
        <span className={classNames(TAG, "bg-[#F2F4F7] text-[#3F4757]")}>
          Based on: {info?.name}
        </span>
        {saved.shared && (
          <span className={classNames(TAG, "bg-[#ECFDF3] text-[#12805C]")}>
            <LuUsers size={11} /> Shared with team
          </span>
        )}
        {changed && saved.mine && (
          <span className={classNames(TAG, "bg-[#FFF4E5] text-[#B45309]")}>
            Unsaved changes
          </span>
        )}
      </div>
      <p className={"mt-1.5 max-w-[760px] text-sm text-[#5B6472]"}>
        {description}
      </p>
      {!saved.mine && saved.createdByName && (
        <p className={"mt-0.5 text-[12.5px] text-[#6B7185]"}>
          Shared by {saved.createdByName}
        </p>
      )}
    </div>
  );

  const menuItem =
    "flex w-full items-center gap-2 rounded-[7px] px-2.5 py-2 text-left text-[13px] font-semibold text-[#3A4256] hover:bg-[#F5F6F8]";
  const actions = (
    <>
      {saved.mine && changed && (
        <button
          type={"button"}
          className={BTN_PRIMARY}
          disabled={busy}
          onClick={saveChanges}>
          <LuCheck size={15} /> Save changes
        </button>
      )}
      <div className={"relative"}>
        <button
          type={"button"}
          aria-label={"More"}
          className={classNames(BTN, "px-3")}
          onClick={() => setMenuOpen((o) => !o)}>
          <LuMoreHorizontal size={16} />
        </button>
        {menuOpen && (
          <div
            className={
              "absolute right-0 top-[46px] z-30 min-w-[210px] rounded-xl border border-[#EDEFF3] bg-white p-1.5 shadow-[0_12px_32px_rgba(20,22,34,0.14)]"
            }>
            {saved.mine && (
              <button
                type={"button"}
                className={menuItem}
                onClick={() => {
                  setMenuOpen(false);
                  setDialog("rename");
                }}>
                <LuPencil size={14} /> Rename
              </button>
            )}
            {saved.mine && (
              <button type={"button"} className={menuItem} onClick={share}>
                <LuUsers size={14} />{" "}
                {saved.shared ? "Stop sharing" : "Share with team"}
              </button>
            )}
            <button
              type={"button"}
              className={menuItem}
              onClick={() => {
                setMenuOpen(false);
                setDialog("copy");
              }}>
              <LuCopy size={14} /> Save a copy
            </button>
            <button
              type={"button"}
              className={menuItem}
              onClick={() => navigate(`/reports/${ctx.report}`)}>
              <LuRotateCcw size={14} /> Open the original report
            </button>
            {(saved.mine || access.isAdmin) && (
              <button
                type={"button"}
                className={classNames(
                  menuItem,
                  "text-[#B42318] hover:bg-[#FEF3F2]",
                )}
                onClick={onDelete}>
                <LuTrash2 size={14} /> Delete
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );

  const note = (
    <>
      {message && (
        <div
          role={"status"}
          className={
            "rounded-xl bg-[#ECFDF3] px-3.5 py-2.5 text-[13px] font-semibold text-[#12805C]"
          }>
          {message}
        </div>
      )}
      {changed && (
        <div
          className={
            "flex flex-wrap items-center gap-2 rounded-xl border border-[#D9D1FF] bg-[#F5F3FF] px-3.5 py-2.5 text-[13px] text-[#3F2EA8]"
          }>
          You changed {parts.join(", ")}.
          {saved.mine
            ? " Click “Save changes” to keep it."
            : " Use “Save a copy” in the ⋯ menu to keep your version."}
          <button
            type={"button"}
            onClick={ctx.reset}
            className={"ml-auto font-bold text-[#6B4EFF] hover:underline"}>
            Undo
          </button>
        </div>
      )}
    </>
  );
  return { header, actions, note, dialogs };
};
